import {
  Tab as MuiTab,
  type TabProps as MuiTabProps,
  Tabs as MuiTabs,
  type TabsProps as MuiTabsProps,
  type Theme,
} from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { useIsMobile } from "../../AppLayout/useResponsive";
import { applyPlatformAwareInteractionStyles } from "../../utils/applyPlatformAwareInteractionStyles";
import { Icon, type IconType } from "../Icon";
import { FloatingNavigationItemWrapper } from "./ItemWrapper";

export const TAB_SIZES = ["normal", "large"] as const;
export type TabSize = (typeof TAB_SIZES)[number];

type BaseTabProps = Omit<MuiTabProps, "variant">;

export interface FloatingNavigationTabProps extends Omit<BaseTabProps, "icon" | "iconPosition"> {
  iconType: IconType;
}

/**
 * Floating navigation tab that appears at the bottom of the page on mobile.
 */
export function FloatingNavigationTab(props: FloatingNavigationTabProps) {
  const { sx, ...restProps } = props;

  return (
    <MuiTab
      {...restProps}
      icon={<Icon type={props.iconType} size="medium" />}
      sx={mergeSxProps(
        (theme: Theme) => ({
          textTransform: "none",
          padding: theme.spacing(2, 3),
          color: theme.palette.text.invert,
          opacity: theme.opacity?.floatingNavigation.default,
          fontWeight: theme.typography.fontWeightMedium,
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
          borderRadius: theme.borderRadius?.xxxLarge,
          paddingX: 2,
          paddingY: 3,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          flexShrink: 0,
          minWidth: "4.5rem",
          zIndex: 2,
          gap: 2,
          marginVertical: 1,

          ...applyPlatformAwareInteractionStyles({
            hoverStyle: {
              color: theme.palette.text.invert,
              opacity: theme.opacity?.floatingNavigation.selected,
            },
            defaultStyle: {
              color: theme.palette.text.subtle,
              opacity: theme.opacity?.floatingNavigation.default,
            },
          }),

          "& .MuiTab-iconWrapper": {
            margin: 0,
          },

          "&.Mui-selected": {
            color: theme.palette.text.invert,
          },
        }),
        sx
      )}
    />
  );
}

export interface TabsProps extends Omit<MuiTabsProps, "variant"> {
  size?: TabSize;
}

/**
 * Floating navigation tabs that appear at the bottom of the page on mobile.
 */
export function FloatingNavigationTabs(props: TabsProps) {
  const { sx, ...restProps } = props;
  const isMobile = useIsMobile();

  if (!isMobile) {
    return null;
  }

  return (
    <FloatingNavigationItemWrapper
      sx={(theme: Theme) => ({
        left: theme.size?.floatingNavigation.margin,
        padding: theme.spacing(1),
        gap: 0,
        width: "auto",
        // to account for menu button
        right: `calc(${theme.size?.floatingNavigation.height} + 2 * ${theme.size?.floatingNavigation.margin})`,
      })}
    >
      <MuiTabs
        {...restProps}
        variant="scrollable"
        scrollButtons={false}
        sx={mergeSxProps(
          (theme: Theme) => ({
            minHeight: "auto",
            height: "100%",
            paddingX: theme.spacing(1),
            width: "100%",

            "& .MuiTabs-flexContainer": {
              gap: theme.spacing(2),
              height: "100%",
            },

            "& .MuiTabs-scroller": {
              overflow: "auto !important",
            },

            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.floatingNavigation!.selectedBackground,
              borderRadius: theme.borderRadius?.xxxLarge,
              zIndex: 1,
              transition: theme.transitions.create(["left", "width"], {
                duration: theme.transitions.duration.standard,
              }),
              height: "auto",
              top: theme.spacing(2),
              bottom: theme.spacing(2),
              marginVertical: 1,
            },
          }),
          sx
        )}
      />
    </FloatingNavigationItemWrapper>
  );
}
